import { t } from 'i18next';
import { useMemo } from 'react';
import { SparkSuspense } from '../../../../components/spark-suspense';
import { useHandlers } from '../../../../context/page-context';
import { PriceListError, type PriceListUpsertError } from '../../errors';
import { usePriceList } from '../../hooks/use-price-list';
import { PriceListFormContext, usePriceListFormLogic } from '../../hooks/use-price-list-form';
import { useUpdatePriceList } from '../../hooks/use-update-price-list';
import type { PriceListBuilderState, PriceListBuilderValidated } from '../../schemas';
import type { PriceListApiResponse, PriceListEdit, PriceListRule } from '../../types';
import { PriceListBuilder } from './price-list-builder';

function transformPriceListToApi(state: PriceListBuilderValidated): PriceListEdit {
  const rules: PriceListRule[] = [];
  if (state.automaticPricing) {
    let adjustPercent = 0;
    let adjustDir: PriceListRule['adjustment_direction'] = 'minus';
    if (state.automaticPricing.percentageVariation) {
      adjustPercent = state.automaticPricing.percentageVariation.amount / 100.0;
      adjustDir = state.automaticPricing.percentageVariation.direction;
    }

    rules.push({
      source_price_list_slug: state.automaticPricing.priceListSource,
      adjustment_percentage: adjustPercent,
      adjustment_direction: adjustDir,
      currency_exchange_rate: state.automaticPricing.exchangeRate,
    });
  }

  return {
    name: state.name,
    rules,
  };
}

function transformPriceListToState(priceList: PriceListApiResponse): PriceListBuilderState {
  const rule = priceList.rules[0] ?? null;

  let automaticPricing:
    | {
        priceListSource: string;
        exchangeRate: string | undefined;
        percentageVariation: {
          amount: string;
          direction: 'minus' | 'plus';
        };
      }
    | undefined;

  if (rule) {
    automaticPricing = {
      priceListSource: rule.source_price_list_slug,
      exchangeRate: rule.currency_exchange_rate?.toString(),
      percentageVariation: {
        amount: (rule.adjustment_percentage * 100.0).toFixed(4),
        direction: rule.adjustment_direction,
      },
    };
  }

  return {
    handle: priceList.slug,
    name: priceList.name,
    currency: priceList.currency_code,
    automaticPricing: automaticPricing,
  };
}

export function PriceListEditForm({
  slug,
  onSuccess,
  onCancel,
}: {
  slug: string;
  onSuccess: () => Promise<void> | void;
  onCancel: () => Promise<void> | void;
}) {
  const { notificationHandler } = useHandlers();

  const { data: priceList, isLoading, isError, refetch } = usePriceList(slug);
  const initialState = useMemo(
    () => (priceList ? transformPriceListToState(priceList) : undefined),
    [priceList],
  );

  const updatePriceList = useUpdatePriceList();

  const priceListForm = usePriceListFormLogic({
    initialState,
    isEditing: true,
    onSubmit: handleSubmit,
    onFailValidation: () => {
      notificationHandler.notify(t('notifications:titles.error'), t('errors:generic'), 'error');
    },
    onSubmitError: handleError,
  });

  function handleError(error: PriceListUpsertError) {
    if (error instanceof PriceListError) {
      notificationHandler.notify(
        t('notifications:titles.error'),
        t(`price_list_builder.notifications.errors.${error.id}`),
        'error',
      );
      return;
    }

    notificationHandler.notify(
      t('notifications:titles.error'),
      t('price_list_builder.notifications.errors.update_failed'),
      'error',
    );
  }

  async function handleSubmit(data: PriceListBuilderValidated) {
    const payload = transformPriceListToApi(data);

    await updatePriceList.mutateAsync(
      { priceList: payload, slug },
      {
        onSuccess: async () => {
          notificationHandler.notify(
            t('notifications:titles.success'),
            t('price_list_builder.notifications.success.update', { name: data.name }),
            'success',
          );

          await onSuccess();
        },
      },
    );
  }

  return (
    <SparkSuspense
      isError={isError}
      isLoading={isLoading || !initialState}
      errorMessage={t('errors:generic')}
      onReload={refetch}
    >
      <PriceListFormContext.Provider value={priceListForm}>
        <PriceListBuilder isEditing onCancel={onCancel} />
      </PriceListFormContext.Provider>
    </SparkSuspense>
  );
}
